import { useEffect } from "react"
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SignalIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { Button, Select } from "~/ui-rtk/components/ui/controls"
import { toCurrencyWithCommas } from "~/ui-rtk/utils/number-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { isSafeHttpUrl } from "~/ui-rtk/utils/validators"
import type {
  TAuditSeverity,
  TDestinationAuditAd,
} from "~/ui-rtk/api/destinationAuditApi"
import useConnect, { CHANNEL_OPTIONS, RANGE_OPTIONS } from "./connect"

const SEVERITY_STYLES: Record<
  TAuditSeverity,
  { chip: string; card: string; Icon: typeof XCircleIcon; label: string }
> = {
  error: {
    chip: "border-system-error-500/40 bg-system-error-500/10 text-system-error-300",
    card: "border-system-error-500/40 hover:border-system-error-500/70",
    Icon: XCircleIcon,
    label: "Error",
  },
  warning: {
    chip: "border-system-warning-500/40 bg-system-warning-500/10 text-system-warning-300",
    card: "border-system-warning-500/40 hover:border-system-warning-500/70",
    Icon: ExclamationTriangleIcon,
    label: "Warning",
  },
  info: {
    chip: "border-system-blue-500/40 bg-system-blue-500/10 text-system-gray-300",
    card: "border-system-blue-600/40 hover:border-system-blue-500/70",
    Icon: InformationCircleIcon,
    label: "Info",
  },
}

function SummaryTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string
  value: string
  hint?: string
  tone?: "neutral" | "bad"
}) {
  return (
    <div className="rounded-xl border border-system-blue-600/40 bg-system-blue-800/40 p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-system-gray-400">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "bad" ? "text-system-error-300" : "text-white",
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-system-gray-500">{hint}</p>}
    </div>
  )
}

function FindingChips({ ad }: { ad: TDestinationAuditAd }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ad.findings.map(finding => (
        <span
          key={finding.code}
          title={finding.message}
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[11px]",
            SEVERITY_STYLES[finding.severity].chip,
          )}
        >
          {finding.code}
        </span>
      ))}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-xl border border-system-blue-600/40 bg-system-blue-800/60"
          />
        ))}
      </div>
      <div className="h-96 animate-pulse rounded-xl border border-system-blue-600/40 bg-system-blue-800/60" />
    </div>
  )
}

export default function DestinationAuditPage() {
  const {
    data,
    isFetching,
    isError,
    refetch,
    range,
    setRange,
    channel,
    setChannel,
    live,
    setLive,
    activeCode,
    setActiveCode,
    visibleAds,
    fragmentedChannels,
    hasCompany,
  } = useConnect()

  useEffect(() => {
    document.title = "Marathon Data: Audits > Destination URLs"
  }, [])

  const summary = data?.summary

  return (
    <div className="min-h-screen">
      <div className="text-start mb-6">
        <Breadcrumbs
          items={[
            { slug: "/audits", name: "Audits" },
            { slug: "destination-urls", name: "Destination URLs" },
          ]}
        />
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">
          Destination URL Audit
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-system-gray-400">
          Every ad with spend in the period, checked on where its click actually
          lands and what tracking rides along. Findings are ranked by the spend
          behind them, so the first row is the most expensive problem — not just
          the most common one.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="w-40">
          <Select
            options={RANGE_OPTIONS}
            value={range}
            onChange={e => setRange(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            options={CHANNEL_OPTIONS}
            value={channel}
            onChange={e => setChannel(e.target.value)}
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant={{ variant: live ? "solid" : "outlined", color: "pink" }}
          onClick={() => setLive(!live)}
          disabled={isFetching}
          title="Fetch each destination to check it resolves. Catches dead pages and homepage redirects."
        >
          <SignalIcon className="h-4 w-4" />
          {live ? "Live link check on" : "Run live link check"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={{ variant: "text" }}
          onClick={() => refetch()}
          disabled={isFetching || !hasCompany}
        >
          <ArrowPathIcon
            className={cn("h-4 w-4", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
        {live && (
          <span className="text-xs text-system-gray-500">
            Live checks fetch each distinct destination — expect this to take a
            while on large accounts.
          </span>
        )}
      </div>

      {isError && (
        <div className="rounded-xl border border-system-error-500/40 bg-system-error-500/10 px-5 py-4 text-sm text-system-error-300">
          The audit could not be loaded. If this company has no paid media
          snapshot yet, there is nothing to audit.
        </div>
      )}

      {isFetching && !data && <LoadingSkeleton />}

      {data && summary && (
        <div className="space-y-8 animate-fadeIn">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryTile
              label="Spend audited"
              value={toCurrencyWithCommas(summary.spendAudited)}
              hint={`${summary.adsAudited.toLocaleString()} ads · ${summary.distinctUrls.toLocaleString()} distinct URLs`}
            />
            <SummaryTile
              label="Spend with findings"
              value={toCurrencyWithCommas(summary.spendWithFindings)}
              hint={`${summary.adsWithFindings.toLocaleString()} ads affected`}
              tone={summary.spendWithFindings > 0 ? "bad" : "neutral"}
            />
            <SummaryTile
              label="Spend with errors"
              value={toCurrencyWithCommas(summary.errorSpend)}
              hint="Untracked, broken, or unreachable destinations"
              tone={summary.errorSpend > 0 ? "bad" : "neutral"}
            />
            <SummaryTile
              label="Live checks run"
              value={summary.urlsChecked.toLocaleString()}
              hint={
                live
                  ? "Distinct URLs fetched, highest spend first"
                  : "Turn on the live link check to test for dead pages"
              }
            />
          </div>

          {/* Coverage, not a fault. Catalog and dynamic ads genuinely report no
              single destination, and blaming them would put the biggest spend
              line in the account under a false "broken" heading. */}
          {summary.adsWithoutDestination > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-system-blue-600/30 bg-system-blue-800/40 px-4 py-3 text-sm text-system-gray-300">
              <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-system-gray-400" />
              <span>
                {summary.adsWithoutDestination.toLocaleString()} ads carrying{" "}
                {toCurrencyWithCommas(summary.spendWithoutDestination)} report
                no single destination URL. That is normal for catalog and
                dynamic ads, which resolve a different URL per product — they
                are counted here but not audited.
              </span>
            </div>
          )}

          {/* Named explicitly rather than quietly dropped: a channel missing from
              the findings should never read as a channel that passed. */}
          {data.uncoveredChannels.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-system-warning-500/30 bg-system-warning-500/5 px-4 py-3 text-sm text-system-gray-300">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-system-warning-400" />
              <span>
                Not audited:{" "}
                {data.uncoveredChannels
                  .map(
                    c =>
                      `${c.channel} (${toCurrencyWithCommas(c.spend)}, ${c.adCount.toLocaleString()} ads)`,
                  )
                  .join(", ")}
                . No destination or tracking data reaches the warehouse for
                these channels — Google Ads, for instance, keeps its tracking in
                `final_url_suffix`, which is not synced. Their spend is excluded
                from the findings below rather than reported as untracked.
              </span>
            </div>
          )}

          <section>
            <div className="mb-4 flex items-center gap-2">
              <span className="h-6 w-1 rounded-full bg-system-brand-pink-500" />
              <h2 className="text-xl font-semibold text-white">Findings</h2>
              {activeCode && (
                <Button
                  type="button"
                  size="xs"
                  variant={{ variant: "text" }}
                  onClick={() => setActiveCode(null)}
                >
                  Clear filter
                </Button>
              )}
            </div>

            {data.issues.length === 0 ? (
              <div className="rounded-xl border border-system-blue-600/40 bg-system-blue-800/40 px-5 py-10 text-center text-sm text-system-gray-400">
                No findings — every audited destination is on the brand domain
                with complete tracking.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.issues.map(issue => {
                  const style = SEVERITY_STYLES[issue.severity]
                  const isActive = activeCode === issue.code
                  return (
                    <button
                      key={issue.code}
                      type="button"
                      onClick={() =>
                        setActiveCode(isActive ? null : issue.code)
                      }
                      className={cn(
                        "rounded-xl border bg-system-blue-800/40 p-4 text-left transition-colors",
                        style.card,
                        isActive && "bg-system-blue-700/60",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <style.Icon className="h-4 w-4 shrink-0 text-system-gray-400" />
                          <p className="font-medium text-white">
                            {issue.title}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium",
                            style.chip,
                          )}
                        >
                          {style.label}
                        </span>
                      </div>
                      <p className="mt-3 text-2xl font-semibold tabular-nums text-white">
                        {toCurrencyWithCommas(issue.spend)}
                      </p>
                      <p className="mt-0.5 text-xs text-system-gray-500">
                        across {issue.adCount.toLocaleString()} ad
                        {issue.adCount === 1 ? "" : "s"}
                      </p>
                      {issue.examples[0] && (
                        <p className="mt-3 line-clamp-2 text-xs text-system-gray-400">
                          {issue.examples[0].findings.find(
                            f => f.code === issue.code,
                          )?.message ?? ""}
                        </p>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </section>

          {data.trackingVariants.length > 1 && (
            <section>
              <div className="mb-2 flex items-center gap-2">
                <span className="h-6 w-1 rounded-full bg-system-brand-pink-500" />
                <h2 className="text-xl font-semibold text-white">
                  Tracking labels in use
                </h2>
              </div>
              <p className="mb-4 max-w-3xl text-sm text-system-gray-400">
                One row per distinct utm_source / utm_medium pair. More than one
                row on the same channel means that channel&apos;s traffic is
                being split across labels in every downstream report.
              </p>
              <div className="overflow-hidden rounded-xl border border-system-blue-600/40 bg-system-blue-800/40">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-system-blue-600/40 bg-system-blue-800/80 text-xs uppercase tracking-wider text-system-gray-400">
                      <th className="px-5 py-3 font-semibold">Channel</th>
                      <th className="px-5 py-3 font-semibold">utm_source</th>
                      <th className="px-5 py-3 font-semibold">utm_medium</th>
                      <th className="px-5 py-3 text-right font-semibold">
                        Ads
                      </th>
                      <th className="px-5 py-3 text-right font-semibold">
                        Spend
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trackingVariants.map(variant => (
                      <tr
                        key={`${variant.channel}-${variant.source}-${variant.medium}`}
                        className="border-b border-system-blue-600/20 last:border-b-0"
                      >
                        <td className="px-5 py-3">
                          <span className="text-white">{variant.channel}</span>
                          {fragmentedChannels.has(variant.channel) && (
                            <span className="ml-2 rounded-md border border-system-warning-500/40 bg-system-warning-500/10 px-1.5 py-0.5 text-[11px] text-system-warning-300">
                              split
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs text-system-gray-300">
                          {variant.source}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs text-system-gray-300">
                          {variant.medium}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-white">
                          {variant.adCount.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-white">
                          {toCurrencyWithCommas(variant.spend)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section>
            <div className="mb-4 flex items-center gap-2">
              <span className="h-6 w-1 rounded-full bg-system-brand-pink-500" />
              <h2 className="text-xl font-semibold text-white">
                {activeCode ? `Ads with ${activeCode}` : "Affected ads"}
              </h2>
              <span className="ml-1 rounded-full bg-system-blue-700/60 px-2 py-0.5 text-xs font-medium text-system-gray-300">
                {visibleAds.length}
              </span>
            </div>

            <div className="overflow-hidden rounded-xl border border-system-blue-600/40 bg-system-blue-800/40">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-system-blue-600/40 bg-system-blue-800/80 text-xs uppercase tracking-wider text-system-gray-400">
                      <th className="px-5 py-3 font-semibold">Ad</th>
                      <th className="px-5 py-3 font-semibold">Destination</th>
                      <th className="px-5 py-3 font-semibold">Findings</th>
                      <th className="px-5 py-3 text-right font-semibold">
                        Spend
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAds.map(ad => (
                      <tr
                        key={`${ad.adId}-${ad.url}`}
                        className="border-b border-system-blue-600/20 transition-colors last:border-b-0 hover:bg-system-blue-700/30"
                      >
                        <td className="max-w-xs px-5 py-4 align-top">
                          <p
                            className="truncate font-medium text-white"
                            title={ad.adName}
                          >
                            {ad.adName || ad.adId}
                          </p>
                          <p
                            className="mt-0.5 truncate text-xs text-system-gray-500"
                            title={ad.campaignName}
                          >
                            {ad.campaignName} · {ad.channel}
                          </p>
                        </td>
                        <td className="max-w-md px-5 py-4 align-top">
                          {!ad.url && (
                            <span className="font-mono text-xs text-system-gray-500">
                              (none reported)
                            </span>
                          )}
                          {/* Only http(s) becomes a link. `ad.url` is
                              advertiser-controlled data arriving from the ad
                              platforms, so a `javascript:` value here would
                              execute in this origin on click — `rel` does not
                              guard against that. The value is still SHOWN,
                              because an INVALID_URL finding is useless if you
                              cannot see the URL it is about. */}
                          {ad.url && isSafeHttpUrl(ad.url) && (
                            <a
                              href={ad.url}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex max-w-full items-center gap-1 truncate font-mono text-xs text-system-gray-300 hover:text-white"
                              title={ad.url}
                            >
                              <span className="truncate">{ad.url}</span>
                              <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0" />
                            </a>
                          )}
                          {ad.url && !isSafeHttpUrl(ad.url) && (
                            <span
                              className="block truncate font-mono text-xs text-system-error-300"
                              title={ad.url}
                            >
                              {ad.url}
                            </span>
                          )}
                          {ad.tracking && (
                            <p
                              className="mt-1 truncate font-mono text-[11px] text-system-gray-500"
                              title={ad.tracking}
                            >
                              {ad.tracking}
                            </p>
                          )}
                          {ad.httpStatus != null && (
                            <p className="mt-1 text-[11px] text-system-gray-500">
                              HTTP {ad.httpStatus}
                              {ad.finalUrl && ad.finalUrl !== ad.url
                                ? ` → ${ad.finalUrl}`
                                : ""}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4 align-top">
                          <FindingChips ad={ad} />
                        </td>
                        <td className="px-5 py-4 text-right align-top tabular-nums text-white">
                          {toCurrencyWithCommas(ad.spend)}
                        </td>
                      </tr>
                    ))}
                    {visibleAds.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-5 py-10 text-center text-sm text-system-gray-400"
                        >
                          No ads to show for this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
